import type {
  WidgetCoreDefinition,
  TeXMLContext,
  TeXMLElement,
  TelnyxEvent,
  CallbackResult,
  ValidationResult,
} from './types';
import { buildCallbackUrl } from './types';

export type TransferConfig = {
  destination: string;
  callerId?: string;
  timeout: number;
  answerOnBridge: boolean;
  ringTone?: string;
  retryWithCalledNumberOnBlock?: boolean;
};

const phoneRegex = /^\+?[1-9]\d{1,14}$/;

const SCREENING_HANGUP_CAUSES = new Set(['607', '608']);

function transferRetryKey(nodeId: string): string {
  return `__transferRetried_${nodeId}`;
}

function normalizePhone(value: string): string {
  return value.replace(/[\s()-]/g, '');
}

function isPhoneNumber(value: string | undefined): boolean {
  return Boolean(value && phoneRegex.test(normalizePhone(value)));
}

function resolveCallerId(config: TransferConfig, context: TeXMLContext): string | undefined {
  if (config.callerId) return config.callerId;
  if (isPhoneNumber(context.variables.callerId)) return undefined;
  const fallbackCallerId = context.phoneNumber || context.variables.calledNumber;
  return isPhoneNumber(fallbackCallerId) ? fallbackCallerId : undefined;
}

function generateTeXML(config: TransferConfig, context: TeXMLContext): TeXMLElement[] {
  const callbackUrl = buildCallbackUrl(context);
  const callerId = resolveCallerId(config, context);

  return [
    {
      tag: 'Dial',
      attributes: {
        action: callbackUrl,
        method: 'POST',
        callerId,
        timeout: config.timeout.toString(),
        answerOnBridge: config.answerOnBridge ? 'true' : 'false',
        ringTone: config.ringTone || undefined,
      },
      children: [
        {
          tag: 'Number',
          content: config.destination,
        },
      ],
    },
  ];
}

export function transferCallbackHandler(
  event: TelnyxEvent,
  config: TransferConfig,
  variables: Record<string, string>,
  context: TeXMLContext,
): CallbackResult {
  const status = event.DialCallStatus;
  if (status === 'completed' || status === 'answered') {
    return { nextHandleId: 'completed' };
  }

  const retryKey = transferRetryKey(context.nodeId);
  const screened = event.SipHangupCause !== undefined && SCREENING_HANGUP_CAUSES.has(event.SipHangupCause);
  const blockedBeforeDial = status === 'failed' && (!event.DialCallSid || event.DialCallSid === 'null');
  const retryEnabled = config.retryWithCalledNumberOnBlock ?? true;
  const calledNumber = context.phoneNumber || context.variables.calledNumber;

  if (retryEnabled && (screened || blockedBeforeDial) && variables[retryKey] !== '1' && isPhoneNumber(calledNumber)) {
    return {
      elements: generateTeXML({ ...config, callerId: calledNumber }, context),
      variables: { [retryKey]: '1' },
    };
  }

  return { nextHandleId: 'failed' };
}

function validateConfig(config: TransferConfig): ValidationResult {
  const errors: string[] = [];

  if (!config.destination.trim()) {
    errors.push('Destination number is required');
  }

  if (config.destination && !phoneRegex.test(normalizePhone(config.destination))) {
    errors.push('Destination must be a valid phone number');
  }

  if (config.callerId && !phoneRegex.test(normalizePhone(config.callerId))) {
    errors.push('Caller ID must be a valid phone number');
  }

  if (config.timeout < 5 || config.timeout > 120) {
    errors.push('Timeout must be between 5 and 120 seconds');
  }

  return { valid: errors.length === 0, errors };
}

export const transferWidgetCore: WidgetCoreDefinition<TransferConfig> = {
  id: 'transfer',
  version: 1,
  name: 'Transfer',
  description: 'Transfer the call to another phone number',
  category: 'routing',
  tier: 'standard',

  defaultConfig: {
    destination: '',
    timeout: 30,
    answerOnBridge: true,
  },

  texml: {
    generate: generateTeXML,
    requiresCallback: true,
  },

  validation: {
    validateConfig,
  },
};
